import type { UseQueryResult } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useState, type ReactNode } from "react";

import { QueryErrorState } from "@/api/QueryErrorState";
import type { ClinicalSummaryItem } from "@/api/hooks";
import {
  useActivateProjectBackgroundMutation,
  useAssetVersionQueries,
  useLibraryPlacementRegistryQuery,
  type AdoptionPin,
  type LibraryAsset,
  type LibraryAssetList,
  type ProjectAdoptions,
} from "@/api/library";
import { isTerminalQueryError } from "@/api/queryClient";
import { useCapability } from "@/auth/SessionContext";
import {
  adoptionLock,
  adoptionRefusal,
  POLICY_UNREAD_SENTENCE,
  type AdoptionDecision,
} from "@/lib/adoptions";
import { storedDomainSummary } from "@/lib/backgroundDomains";
import { storedAdoptionRows } from "@/lib/sectionAdoption";
import { AdoptionDecisionDialog } from "@/ui/adoptions/AdoptionDecisionDialog";
import { BackgroundProfileControl } from "@/ui/adoptions/BackgroundProfileControl";
import { PreflightReasons } from "@/ui/adoptions/PreflightReasons";
import { Button } from "@/ui/Button";
import { Dialog, DialogContent } from "@/ui/Dialog";
import { Notice } from "@/ui/Notice";
import { ProjectSectionPane } from "@/ui/project/ProjectSectionPane";
import {
  SectionReviewList,
  type SectionReviewActions,
} from "@/ui/review/SectionReviewList";
import { EmptyState, LoadingState, StaleDataNotice } from "@/ui/states";
import { Tooltip } from "@/ui/Tooltip";

/**
 * The canonical Project BG-set surface: one compact row per independently
 * adopted library asset, plus one multi-select adoption picker.
 *
 * Both halves are the join of the project's pin set and the service catalog;
 * they do not depend on the clinical engine composing the pinned content. No
 * BG-set names or ids are mirrored here. The picker is filtered by type,
 * lifecycle and existing pins, and writes the exact immutable head version it
 * displays.
 *
 * ## AUTHORED DOMAINS ARE ADOPTABLE SINCE #857/#859
 *
 * The picker used to require `capabilities.seeded`, because an authored BG set
 * had no runtime placement contract and "presenting one as adoptable would make
 * a later freeze fail after a clinician had already been told it worked". #857
 * supplied that contract — an authored domain's coordinate is
 * `<profile variant>/<domain id>`, it composes into that variant's own domains
 * directory, and it claims its line in the project's `active_domains.yaml`
 * roster — and #859 supplies the surface that authors one. A picker that still
 * hid them would make the New-domain journey end in a library asset no project
 * could ever use.
 *
 * THE ROSTER RULES ARE THE SERVICE'S, and they are the reason nothing is
 * pre-filtered beyond lifecycle. `adoption_preflight` refuses a pin set whose
 * domains span two runtime directories or share one interview position, with
 * `placement.background_roster_conflict` — a vocabulary code
 * `AdoptionDecisionDialog` already renders. Re-deriving either rule here would
 * be a second copy free to disagree with the one that actually refuses, and
 * would hide a legitimately adoptable domain behind a browser's guess about
 * runtime composition.
 *
 * ## ROWS ARE IN INTERVIEW ORDER, NOT PIN ORDER
 *
 * "The runtime roster derives from independent pins and domain order, not
 * catalog sort" (S7 contract §4). A domain's position lives in its stored
 * document, so the exact pinned version of each row is read and the rows are
 * sorted by what the runtime will actually read. A version that has not been
 * read yet — or could not be — keeps its pin-set position and says so, rather
 * than being silently sorted as though it had position zero.
 */
/**
 * One selectable row of the project's background selection.
 *
 * A row is NOT a catalog asset: it is either one the catalog named, or a held
 * pin the catalog did not — and both have to be submittable, or a write that
 * touches one visible row silently drops the invisible one.
 */
interface SelectionRow {
  id: string;
  /** The catalog's name, or the exact id the pin set holds. */
  name: string;
  assetType: "background_set" | "background_profile";
  /** The version this selection would send: the held one, else the head. */
  version: string;
  /** Whether the catalog named it, which is what the row may claim about it. */
  named: boolean;
}

export function BgSetAdoptionPicker({
  projectId,
  catalog,
  adoptions,
  sectionLabel = "BG sets",
  controls,
}: {
  projectId: string;
  catalog: UseQueryResult<LibraryAssetList>;
  adoptions: UseQueryResult<ProjectAdoptions>;
  /**
   * The section's own name from the rail, for the pane's accessible heading
   * and the action's accessible name (#909).
   *
   * Defaulted rather than required: this component serves exactly one rail
   * slug, and every existing prop-driven test mounts it without one.
   */
  sectionLabel?: string;
  /**
   * Section-scoped controls above the rows, or absent (#909).
   *
   * The stable insertion point `ProjectSectionPane` documents. BG sets is the
   * section the BG profile selection (#858, #914) lands in, and it lands here
   * rather than by rearranging this component.
   */
  controls?: ReactNode;
}) {
  const write = useCapability("draft.write");
  // The SERVED cardinality policy (#634). A pin on a family the server bounds
  // at one is a replacement, and `AdoptionDecisionDialog` cannot describe which
  // pin it displaces without this document — so an unread policy becomes a
  // STATED refusal on the adopt control rather than a control whose only
  // outcome is that refusal (ADR 0001). Removal is deliberately not gated on
  // it: an un-pin is the recovery path out of every degraded state (#612).
  const placementRegistry = useLibraryPlacementRegistryQuery();
  const policyUnread = !placementRegistry.isSuccess;
  // THE ACTIVATION, NOT THE WHOLE-SET REPLACEMENT (#914). `PUT /adoptions` may
  // not touch the draft, so it cannot select the variant an authored BG pin
  // needs: `placement.reachability` refuses every authored domain while the
  // project's `flow_config.bg_variant` still names the engine's baseline, which
  // for a project that has never chosen one is always. `POST
  // /background-activation` derives `rules.bg_variant` and `rules.bg_categories`
  // from these exact pins' own server-owned coordinates and writes pins and
  // registration together — or neither.
  const replace = useActivateProjectBackgroundMutation(projectId);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [decision, setDecision] = useState<AdoptionDecision | null>(null);

  const readsCurrent = catalog.isSuccess && adoptions.isSuccess;
  const pinned = new Set(
    adoptions.isSuccess
      ? adoptions.data.items.map((item) => item.assetId)
      : [],
  );
  // THE PIN SET SPLIT BY FAMILY, from the pin's own `assetType` rather than
  // from the catalog: the pin set is authoritative about what this project
  // holds and it answers even when the catalog did not (#615).
  //
  // The profile is separated here and stays separated everywhere below — out of
  // the domain rows, out of the row-enrichment map, and into its own control
  // above the list. "No duplicate domain list" is the contract's requirement
  // for this screen; "no profile among the areas" is the same rule from the
  // other side.
  const heldPins: readonly AdoptionPin[] = adoptions.isSuccess
    ? adoptions.data.items
    : [];
  const profilePin =
    heldPins.find((pin) => pin.assetType === "background_profile") ?? null;
  const domainPins = heldPins.filter(
    (pin) => pin.assetType === "background_set",
  );
  // The EXACT version each held pin sits at. The selection edits WHICH assets
  // the project holds, never which version of one — moving a pin forward is the
  // row menu's decision, taken against its own impact — so a checked held row
  // is submitted at the version it is held at, never the library head.
  const heldVersions = new Map(
    heldPins.map((pin) => [pin.assetId, pin.version] as const),
  );
  // The picker is a write surface, so it consumes only current successful
  // reads. In particular, an already-open dialog must not retain cached
  // candidate names after a terminal catalog refetch refuses access.
  // EVERY BACKGROUND ASSET THIS SELECTION MAY NAME — the held ones included.
  //
  // This used to be "assets not already pinned", which made the dialog an
  // add-only control and left two states unreachable (#914 review):
  //
  //   * NO WAY BACK TO THE BASE PACK. From a profile plus its domains the
  //     server accepts exactly one exit — the EMPTY set. Dropping the profile is
  //     `unbacked_domains`; dropping domains one at a time is accepted until the
  //     last, which leaves a `lonely_profile`. An add-only dialog can express
  //     none of that.
  //   * NO PROFILE REPLACEMENT. Coordinates are unique per family, so another
  //     profile is necessarily another VARIANT, and the domains have to move
  //     with it. Hiding profile candidates while one is pinned did keep the
  //     served cardinality bound, but by removing the control rather than by
  //     making the write atomic.
  //
  // `POST /background-activation` takes the COMPLETE resulting background set,
  // so the honest surface is one that lets a clinician state it: what is held is
  // checked, unchecking removes it, and the profile behaves as a single choice.
  // Verified against the real service — the complete swap (new profile + its own
  // domains, the old set unchecked) is accepted.
  //
  // A HELD PIN IS LISTED WHATEVER ITS LIFECYCLE. An archived asset cannot be
  // newly pinned (`placement.asset_archived`) and is therefore not offered — but
  // one this project already holds has to stay visible, or removing it would be
  // the state this rewrite exists to make reachable, hidden again.
  const catalogChoices = readsCurrent
    ? catalog.data.items.filter(
        (
          asset,
        ): asset is Extract<
          LibraryAsset,
          { assetType: "background_set" | "background_profile" }
        > =>
          (asset.assetType === "background_set" ||
            asset.assetType === "background_profile") &&
          (pinned.has(asset.id) || !asset.capabilities.archived),
      )
    : [];

  // EVERY ROW THIS SELECTION OFFERS, and the crucial half is the second: a held
  // pin the catalog did not name still gets one.
  //
  // `storedAdoptionRows` exists precisely because a stored pin can be absent
  // from an otherwise successful catalog response, and this dialog has to
  // respect the same fact (#914 review). Deriving the submitted set from the
  // catalog alone left such a pin checked, invisible and OUT of the payload —
  // so changing any visible item silently removed a live pin.
  //
  // A recovery row carries the identity the PIN SET holds and its exact held
  // version, which is all the project knows about it; it can still be
  // unchecked, because an un-pin is the recovery path out of every degraded
  // state (#612).
  const candidates: SelectionRow[] = [
    ...catalogChoices.map((asset) => ({
      id: asset.id,
      name: asset.name,
      assetType: asset.assetType,
      version: heldVersions.get(asset.id) ?? asset.latestVersion,
      named: true,
    })),
    ...heldPins
      .filter(
        (
          pin,
        ): pin is AdoptionPin & {
          assetType: "background_set" | "background_profile";
        } =>
          (pin.assetType === "background_set" ||
            pin.assetType === "background_profile") &&
          !catalogChoices.some((asset) => asset.id === pin.assetId),
      )
      .map((pin) => ({
        id: pin.assetId,
        name: pin.assetId,
        assetType: pin.assetType,
        version: pin.version,
        named: false,
      })),
  ].sort((left, right) => left.name.localeCompare(right.name));

  // WHAT THE PROJECT HOLDS, as a set — the dialog's starting point and the
  // thing "changed" is measured against.
  const heldBackgroundIds = new Set(
    heldPins
      .filter(
        (pin) =>
          pin.assetType === "background_set" ||
          pin.assetType === "background_profile",
      )
      .map((pin) => pin.assetId),
  );
  const selectionChanged =
    selected.size !== heldBackgroundIds.size ||
    [...selected].some((id) => !heldBackgroundIds.has(id));

  const refusal =
    write.reason ??
    (!catalog.isSuccess || !adoptions.isSuccess
      ? "The library and this project's current adoptions must load before another BG set can be adopted."
      : policyUnread
        ? POLICY_UNREAD_SENTENCE
        : null);

  const terminalCatalogFailure =
    catalog.isError && isTerminalQueryError(catalog.error);
  // A transient failed refresh may show its last authorized response with a
  // stale notice. A terminal refusal, especially 401/403, discards that cache.
  const readableCatalog = terminalCatalogFailure ? undefined : catalog.data;
  const backgroundAssets = new Map(
    (readableCatalog?.items ?? [])
      .filter((asset) => asset.assetType === "background_set")
      .map((asset) => [asset.id, asset]),
  );
  // The catalog row for the held profile, kept OUT of `backgroundAssets` so it
  // cannot enrich a domain row: the profile is presented by its own control
  // above the list, never as a seventh row among six areas.
  const profileAsset =
    profilePin === null
      ? null
      : ((readableCatalog?.items ?? []).find(
          (asset) => asset.id === profilePin.assetId,
        ) ?? null);
  // Asset id -> the name shown on the row, so a refusal names the domain the
  // clinician picked rather than an opaque id. Built from the pins AND the
  // candidates, because a refusal can name either half of the submitted set.
  const candidateNames = new Map<string, string>([
    ...[...backgroundAssets].map(
      ([id, asset]) => [id, asset.name] as [string, string],
    ),
    // THE HELD PROFILE TOO, which is in neither of the other two: it is kept out
    // of `backgroundAssets` so it cannot enrich a domain row, and out of
    // `candidates` because it is already pinned. A refusal names it whenever
    // the submitted domains stop describing one complete interview, and without
    // this that line would carry an opaque id.
    ...(profileAsset ? [[profileAsset.id, profileAsset.name] as [string, string]] : []),
    ...candidates.map((row) => [row.id, row.name] as [string, string]),
  ]);

  // Row identity belongs to the stored pin set, not the catalog (#615). The
  // catalog enriches a row where it answered; it never decides whether a
  // successfully read pin exists on screen. This is the same recovery-row
  // derivation the engine-composed section surfaces use.
  const stored: ClinicalSummaryItem[] = adoptions.isSuccess
    ? storedAdoptionRows(
        "bg-sets",
        // DOMAINS ONLY. `storedAdoptionRows` derives rows from every family the
        // section adopts, and this section adopts two — so without this the
        // interview profile would render as a seventh row among the six areas,
        // with an interview position it does not have and a row menu offering
        // the wrong sentence. It is presented above instead.
        domainPins,
        readableCatalog?.items ?? null,
      ).map((item) => {
        const asset = backgroundAssets.get(item.id);
        return asset
          ? {
              ...item,
              name: asset.name,
              latestVersion: asset.latestVersion,
              attention:
                item.adoptedVersion === asset.latestVersion
                  ? []
                  : ["outdated_adoption"],
            }
          : item;
      })
    : [];

  // The EXACT PINNED version of each row, which is where a domain's interview
  // position lives. Never the library head: the two are different documents the
  // moment somebody publishes, and the order this project runs is the order its
  // pins declare.
  const pinnedVersions = useAssetVersionQueries(
    domainPins.map((pin) => ({ assetId: pin.assetId, version: pin.version })),
  );
  const positions = new Map<string, number>();
  for (const item of stored) {
    const detail = pinnedVersions.byAssetId.get(item.id);
    const order = detail ? storedDomainSummary(detail.content)?.order : null;
    if (typeof order === "number") {
      positions.set(item.id, order);
    }
  }
  // ONLY THE ROWS WHOSE POSITION IS KNOWN MOVE, and they move CORRECTLY.
  //
  // This was a comparator returning 0 whenever either side had no position, on
  // the intuition that "unknown compares equal to everything" leaves it where
  // it is. It does not: 0 is not a consistent ordering, so `Array.sort` may
  // never compare two known positions that an unknown one sits between —
  // `3, unknown, 1` stays in that order — while the interview-order line above
  // the list says it is sorted. A surface that names an order has to produce
  // one.
  //
  // So the known-position rows are sorted AMONG THEMSELVES and written back
  // into the slots they occupied. Every unknown row keeps its exact pin-set
  // place, which is what the notice below it describes, and the known ones are
  // in interview order relative to each other whatever sits between them.
  const rows = [...stored];
  const knownSlots = rows
    .map((item, index) => (positions.has(item.id) ? index : -1))
    .filter((index) => index >= 0);
  const ordered = knownSlots
    .map((index) => rows[index] as ClinicalSummaryItem)
    .sort((left, right) => (positions.get(left.id) ?? 0) - (positions.get(right.id) ?? 0));
  knownSlots.forEach((slot, at) => {
    rows[slot] = ordered[at] as ClinicalSummaryItem;
  });
  const unordered = stored.length - positions.size;
  // THE INTERVIEW SEQUENCE the profile control states: the SAME rows, in the
  // SAME order, named the same way. Derived from `rows` rather than computed a
  // second time, so the compact line above the list and the list itself cannot
  // disagree about what this project asks or in what order.
  const sequence = rows.map((item) => ({
    id: item.id,
    name: item.name,
    order: positions.get(item.id) ?? null,
  }));

  const actions: SectionReviewActions | undefined =
    catalog.isSuccess && adoptions.isSuccess
      ? {
          onDecide: setDecision,
          byItemId: new Map(
            rows.map((item) => {
              const asset = backgroundAssets.get(item.id);
              const lock = adoptionLock(asset, undefined);
              return [
                item.id,
                {
                  adoptVersion: asset?.latestVersion ?? null,
                  adoptRefusal: adoptionRefusal("pin", {
                    asset,
                    lock,
                    held: true,
                    missingWriteReason: write.reason,
                    policyUnread,
                  }),
                  held: true,
                  removeRefusal: adoptionRefusal("unpin", {
                    asset,
                    lock,
                    held: true,
                    missingWriteReason: write.reason,
                    // NEVER the policy on a removal: the un-pin is the recovery path
                    // out of every degraded state, and no read may take it away
                    // (#612).
                    policyUnread: false,
                  }),
                },
              ];
            }),
          ),
        }
      : undefined;

  function toggle(assetId: string, checked: boolean) {
    // A REFUSAL DESCRIBES THE SET THAT WAS SENT, not the one now on screen.
    // The service answers per pin — "these areas cannot run together", "this
    // profile and these areas do not describe one interview" — so leaving it
    // rendered while the clinician edits the boxes presents a verdict about a
    // selection that no longer exists as though it were current.
    if (replace.isError) {
      replace.reset();
    }
    setSelected((current) => {
      const next = new Set(current);
      if (!checked) {
        next.delete(assetId);
        return next;
      }
      // ONE PROFILE, kept by CONSTRUCTION rather than by hiding the others: the
      // served bound is one adopted profile, so checking a second replaces the
      // first in the set this dialog will send. That is what makes a profile
      // swap an atomic replacement instead of a refused second pin.
      if (
        candidates.find((row) => row.id === assetId)?.assetType ===
        "background_profile"
      ) {
        for (const row of candidates) {
          if (row.assetType === "background_profile") {
            next.delete(row.id);
          }
        }
      }
      next.add(assetId);
      return next;
    });
  }

  async function adoptSelected() {
    if (!readsCurrent || policyUnread || !selectionChanged) {
      return;
    }
    try {
      await replace.mutateAsync({
        expectedAdoptionsVersion: adoptions.data.adoptionsVersion,
        // THE COMPLETE BACKGROUND SET, exactly as checked — nothing carried
        // through implicitly. An unchecked held pin is REMOVED by its absence,
        // which is what makes "return this project to the base pack" (an empty
        // set) and a profile swap expressible at all.
        //
        // Only this section's two families travel: the activation preserves
        // every other family's pins server-side and refuses a body naming one.
        items: candidates
          .filter((row) => selected.has(row.id))
          .map((row) => ({ assetId: row.id, version: row.version })),
      });
      setOpen(false);
    } catch {
      // The refusal is rendered in the dialog beside the unchanged selection.
    }
  }

  return (
    <>
      {/*
        THE SHARED PANE (#909). This section already carried the reference
        arrangement — the right-aligned action above compact rows — as its own
        private layout. Rendering it through `ProjectSectionPane` is what makes
        that arrangement the product's, not this component's: the same shell,
        the same insertion points and the same accessible heading as every
        other Project section, so a control added to one is added to all of
        them the same way.
      */}
      <ProjectSectionPane
        sectionLabel={sectionLabel}
        controls={
          <div className="space-y-4">
            {/* #909's declared `controls` slot, filled by the leaf it was
                declared for. The profile is read and changed ABOVE the areas
                because it decides how every one of them is asked — "a clinical
                author meets them before the list", which is the slot's own
                stated reason for being above rather than below. */}
            <BackgroundProfileControl
              projectId={projectId}
              adoptionsVersion={
                adoptions.isSuccess ? adoptions.data.adoptionsVersion : "0"
              }
              profilePin={profilePin}
              profileAsset={profileAsset}
              domainPins={domainPins}
              sequence={sequence}
              readsCurrent={readsCurrent}
              adoptionsCurrent={adoptions.isSuccess}
              policyUnread={policyUnread}
            />
            {controls}
          </div>
        }
        primaryAction={
          <Tooltip
            content={refusal ?? "Adopt BG sets from the Clinical library"}
          >
            <Button
              size="sm"
              disabled={refusal !== null}
              onClick={() => {
                // PRE-CHECKED WITH WHAT IS HELD, because the dialog now states
                // the complete resulting set rather than a list of additions.
                setSelected(new Set(heldBackgroundIds));
                // …and a refusal from a previous visit is not this visit's.
                replace.reset();
                setOpen(true);
              }}
            >
              <Plus aria-hidden="true" className="h-4 w-4" />
              Adopt from library
            </Button>
          </Tooltip>
        }
      >
        {adoptions.isPending ? (
          <LoadingState label="Loading BG set adoptions" rows={3} />
        ) : adoptions.isError ? (
          <QueryErrorState
            error={adoptions.error}
            subject="Adoptions"
            title="This project's BG sets could not be loaded"
            description="The Editor Service did not return this project's adoption list, so no BG-set row is inferred from cached or catalog data."
            onRetry={() => void adoptions.refetch()}
          />
        ) : (
          <>
            {catalog.isPending ? (
              <Notice>
                The Clinical library catalog is still loading. Every stored BG
                set and its exact library page remain available below; names,
                library-head comparisons, and adoption controls appear after the
                catalog answers.
              </Notice>
            ) : catalog.isError && catalog.data && !terminalCatalogFailure ? (
              <StaleDataNotice
                updatedAt={catalog.dataUpdatedAt}
                onRetry={() => void catalog.refetch()}
              />
            ) : catalog.isError ? (
              <QueryErrorState
                error={catalog.error}
                subject="Library assets"
                title="The adopted BG sets could not be named"
                description="The Clinical library catalog did not load. Every stored BG-set pin remains listed below by its exact id, family, version, and library page, without catalog-only facts or write controls."
                onRetry={() => void catalog.refetch()}
              />
            ) : null}

            {rows.length === 0 ? (
              <EmptyState
                title="No BG sets adopted yet"
                description="Adopt a BG set from the Clinical library to add it to this project's configuration."
              />
            ) : (
              <>
                {unordered > 0 ? (
                  // THE ORDER ON SCREEN IS NOT THE RUNTIME ORDER while a
                  // position is unknown, and saying so is the difference
                  // between a list a clinician can trust and one they cannot.
                  // No retry control: `SectionReviewList` below is complete
                  // without these reads, and the rows recover on their own.
                  <Notice>
                    {unordered === stored.length
                      ? "The interview order of these domains is not shown yet: their pinned content has not been read. They are listed in the order they were adopted."
                      : `${unordered} of these domains could not have their interview position read, so they are listed in the order they were adopted.`}
                  </Notice>
                ) : null}
                <SectionReviewList
                  items={rows}
                  sectionLabel={sectionLabel}
                  {...(actions ? { actions } : {})}
                />
              </>
            )}
          </>
        )}
      </ProjectSectionPane>

      {open ? (
        <Dialog
          open
          onOpenChange={(next) => {
            if (!next && !replace.isPending) {
              setOpen(false);
            }
          }}
        >
          <DialogContent
            busy={replace.isPending}
            title="Adopt from library — BG sets"
            description="This project's background, as one selection: the areas it asks and the interview profile they are asked under. What it holds now is checked; unchecking removes it. A profile and its areas have to be adopted together — the clinical engine cannot run one without the other — and clearing every box returns the background to the base clinical pack's."
            footer={
              <div className="flex flex-wrap justify-end gap-2">
                <Button
                  variant="outline"
                  disabled={replace.isPending}
                  onClick={() => setOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  disabled={
                    !readsCurrent ||
                    policyUnread ||
                    // NOT `size === 0`: an empty selection is a legitimate write
                    // — it is the ONE accepted exit from a profile-plus-domains
                    // set, and refusing it here is what left the section with no
                    // path back to the base pack. What is refused is a submit
                    // that would change nothing.
                    !selectionChanged ||
                    replace.isPending
                  }
                  onClick={() => void adoptSelected()}
                >
                  Save selection
                </Button>
              </div>
            }
          >
            {policyUnread ? (
              <Notice role="alert">{POLICY_UNREAD_SENTENCE}</Notice>
            ) : !readsCurrent ? (
              <Notice role="alert">
                The library and this project&apos;s current adoptions must load
                again before this selection can be applied. Nothing will be
                submitted from cached rows.
              </Notice>
            ) : candidates.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">
                The shared clinical library holds no background areas or
                interview profiles yet.
              </p>
            ) : (
              <ul className="divide-y divide-border overflow-hidden rounded-md border border-border">
                {candidates.map((row) => (
                  <li key={row.id}>
                    <label className="flex cursor-pointer items-center gap-3 px-3 py-3 hover:bg-secondary/60">
                      <input
                        type="checkbox"
                        checked={selected.has(row.id)}
                        onChange={(event) =>
                          toggle(row.id, event.currentTarget.checked)
                        }
                        className="h-4 w-4 accent-primary"
                      />
                      <span className="min-w-0 flex-1 text-sm font-medium text-foreground">
                        <span className={row.named ? "" : "font-mono text-xs"}>
                          {row.name}
                        </span>
                        {row.assetType === "background_profile" ? (
                          // WHICH KIND OF THING THIS ROW IS, because the two
                          // families are genuinely different choices and the
                          // names alone would not say so. Only the profile is
                          // marked: a background area is what this list has
                          // always held, and marking both would be noise.
                          <span className="ml-2 font-normal text-xs text-muted-foreground">
                            Interview profile
                          </span>
                        ) : null}
                        {heldBackgroundIds.has(row.id) ? (
                          <span className="ml-2 font-normal text-xs text-muted-foreground">
                            Adopted
                          </span>
                        ) : null}
                        {row.named ? null : (
                          // THE CATALOG DID NOT NAME IT, said rather than left
                          // to be guessed from an id: the pin is real and the
                          // project's, and unchecking it is still a supported
                          // recovery.
                          <span className="ml-2 font-normal text-xs text-muted-foreground">
                            Not in the library catalog
                          </span>
                        )}
                      </span>
                      {/* THE VERSION THIS SELECTION WOULD SEND, which for a
                          held pin is the version it is HELD at rather than the
                          library head. The dialog decides which assets the
                          project holds; moving a pin forward is the row menu's
                          own decision, taken against its own impact. */}
                      <span className="text-xs text-muted-foreground">
                        {row.version}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            )}

            {replace.isError ? (
              // THE PER-PIN REASON, not just the envelope's sentence.
              //
              // `409 adoption_preflight_refused` carries `details.refusals[]`,
              // each naming the pin and a `PlacementRefusalCode` — vocabulary
              // this shell already has a label for. Before authored domains
              // were adoptable this path was effectively unreachable from the
              // picker, and its one line ("One or more pins cannot be adopted")
              // told a clinician nothing they could act on. It is now the FIRST
              // refusal the New-domain journey meets: a domain placed in one
              // profile variant cannot join a roster resolving in another, and
              // that is a sentence, not a mystery.
              //
              // The label is rendered ONLY for a code this bundle knows. An
              // unrecognized one falls back to the envelope's message rather
              // than to the raw token, which is the same rule every other
              // vocabulary read in this shell follows.
              <Notice role="alert" tone="destructive" className="mt-3">
                <p>
                  {replace.error instanceof Error
                    ? replace.error.message
                    : "The Editor Service refused the adoption. Nothing was changed."}
                </p>
                <PreflightReasons error={replace.error} names={candidateNames} />
              </Notice>
            ) : null}
          </DialogContent>
        </Dialog>
      ) : null}

      {decision ? (
        <AdoptionDecisionDialog
          projectId={projectId}
          decision={decision}
          unpinPresentation="project-row"
          onClose={() => setDecision(null)}
        />
      ) : null}
    </>
  );
}
